/**
 * Gap Spot Detection Utilities (Phase 2c)
 *
 * Detects which spots a gap spot depends on by finding spots that:
 * 1. Spatially overlap with the gap spot
 * 2. Are larger than the gap spot (based on allowedSizes)
 */

import { Shape, SpotMetadata, SizeCategory } from "../RampCanvasV2";

/**
 * Size category ranking (smaller to larger)
 * Used to compare spot sizes
 */
const SIZE_RANKING: Record<SizeCategory, number> = {
  "very-small": 1,
  "small": 2,
  "midsize": 3,
  "super-midsize": 4,
  "large": 5,
};

/**
 * Get the maximum size category allowed by a spot
 *
 * @param spot - The spot to check
 * @returns The largest size category in allowedSizes, or "very-small" if none
 */
function getMaxSizeCategory(spot: Shape): SizeCategory {
  const metadata = spot.metadata as SpotMetadata;
  const allowedSizes = metadata?.allowedSizes || [];

  if (allowedSizes.length === 0) {
    return "very-small";
  }

  // Find the largest category
  let maxRank = 0;
  let maxCategory: SizeCategory = "very-small";

  for (const size of allowedSizes) {
    const rank = SIZE_RANKING[size] || 0;
    if (rank > maxRank) {
      maxRank = rank;
      maxCategory = size;
    }
  }

  return maxCategory;
}

/**
 * Check if spot1 is larger than spot2
 *
 * @param spot1 - First spot
 * @param spot2 - Second spot
 * @returns true if spot1's max allowed size is larger than spot2's
 */
export function isLargerSpot(spot1: Shape, spot2: Shape): boolean {
  const size1 = getMaxSizeCategory(spot1);
  const size2 = getMaxSizeCategory(spot2);

  return SIZE_RANKING[size1] > SIZE_RANKING[size2];
}

/**
 * Check if two spot polygons overlap spatially
 * Uses simple bounding box intersection check
 *
 * @param spot1 - First spot
 * @param spot2 - Second spot
 * @returns true if the spots' bounding boxes overlap
 */
export function spotsOverlap(spot1: Shape, spot2: Shape): boolean {
  if (!spot1.points || spot1.points.length === 0) return false;
  if (!spot2.points || spot2.points.length === 0) return false;

  // Get bounding boxes
  const box1 = getBoundingBox(spot1);
  const box2 = getBoundingBox(spot2);

  // Check if bounding boxes overlap
  // Boxes overlap if they intersect on both axes
  const xOverlap = box1.minX <= box2.maxX && box1.maxX >= box2.minX;
  const yOverlap = box1.minY <= box2.maxY && box1.maxY >= box2.minY;

  return xOverlap && yOverlap;
}

/**
 * Get bounding box for a spot
 */
function getBoundingBox(spot: Shape): { minX: number; maxX: number; minY: number; maxY: number } {
  const xs = spot.points.map(p => p.x);
  const ys = spot.points.map(p => p.y);

  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

/**
 * Find spots that overlap with target spot AND are larger
 *
 * Algorithm:
 * 1. Filter to Spot type shapes only
 * 2. Exclude the target spot itself
 * 3. Filter to spots that spatially overlap target spot
 * 4. Filter to spots that are larger (compare allowedSizes max category)
 * 5. Return list of spot IDs
 *
 * @param targetSpot - The gap spot to analyze
 * @param allSpots - All spots in the ramp
 * @returns Array of spot IDs that the gap spot depends on
 */
export function detectRequiredSpots(
  targetSpot: Shape,
  allSpots: Shape[]
): string[] {
  // Only analyze Spot type shapes
  if (targetSpot.type !== "Spot") {
    return [];
  }

  const requiredSpots = allSpots
    .filter(spot => {
      // Must be a Spot type
      if (spot.type !== "Spot") return false;

      // Exclude self
      if (spot.id === targetSpot.id) return false;

      // Must spatially overlap
      if (!spotsOverlap(targetSpot, spot)) return false;

      // Must be larger
      if (!isLargerSpot(spot, targetSpot)) return false;

      return true;
    })
    .map(spot => spot.id);

  return requiredSpots;
}
